import React, { useState } from 'react';
import { ClipboardCopy, Spinner } from '@patternfly/react-core';
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, TimesIcon } from '@patternfly/react-icons';
import { usePreviewJobStatus } from '../context/PreviewJobStatusContext';

const C = {
  green: '#3E8635',
  red: '#C9190B',
  amber: '#F0AB00',
  blue: '#0052CC',
  border: 'var(--border, #d2d2d2)',
  bg: 'var(--surface, #fff)',
  muted: 'var(--text-secondary, #6a6e73)',
};

// Persistent, app-wide indicator for a running/completed preview deploy job.
// Lives at the AppLayout level so it stays visible while navigating away from
// the Preview Environment wizard. Collapses to a small badge; expands on click.
export const PreviewJobStatusWidget: React.FC = () => {
  const { isDeploying, jobUrl, deployError, statusMessage, sessionStatus, timeoutReason, previewTag, dismissed, dismiss } = usePreviewJobStatus();
  const [expanded, setExpanded] = useState(false);

  const hasJob = isDeploying || jobUrl !== null || deployError !== null;
  if (!hasJob || dismissed) return null;

  const isLive = sessionStatus === 'running';
  const isFailed = sessionStatus === 'failed' || deployError !== null;
  const isTimeout = sessionStatus === 'timeout';
  const isPending = !isDeploying && jobUrl !== null && !sessionStatus;

  let label = 'Preview starting…';
  let color = C.blue;
  let icon: React.ReactNode = <Spinner size="sm" />;
  if (isDeploying) {
    label = 'Deploying preview…';
  } else if (isLive) {
    label = 'Preview live';
    color = C.green;
    icon = <CheckCircleIcon />;
  } else if (isFailed) {
    label = 'Preview deploy failed';
    color = C.red;
    icon = <ExclamationCircleIcon />;
  } else if (isTimeout) {
    label = 'Preview delayed';
    color = C.amber;
    icon = <ExclamationTriangleIcon />;
  } else if (isPending) {
    label = 'Preview starting…';
  }

  const routingHeader = previewTag ? `Baggage: mirrord-session=${previewTag}` : '';

  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999, fontSize: 13 }}>
      {expanded && (
        <div
          style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
            padding: '12px 14px',
            width: 340,
            marginBottom: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color, fontWeight: 600 }}>
              {icon}
              <span>{label}</span>
            </div>
            <button
              aria-label="Dismiss"
              onClick={() => { setExpanded(false); dismiss(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 2 }}
            >
              <TimesIcon />
            </button>
          </div>

          {(isDeploying || isPending) && statusMessage && (
            <div style={{ color: C.muted, marginBottom: 8 }}>{statusMessage}</div>
          )}
          {(isFailed || isTimeout) && (deployError || timeoutReason) && (
            <div style={{ color: C.muted, marginBottom: 8 }}>{deployError || timeoutReason}</div>
          )}

          {jobUrl && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ color: C.muted, marginBottom: 4 }}>Jenkins Job</div>
              <ClipboardCopy isReadOnly hoverTip="Copy" clickTip="Copied">
                {jobUrl}
              </ClipboardCopy>
            </div>
          )}

          {isLive && routingHeader && (
            <div>
              <div style={{ color: C.muted, marginBottom: 4 }}>Routing Header</div>
              <ClipboardCopy isReadOnly hoverTip="Copy" clickTip="Copied">
                {routingHeader}
              </ClipboardCopy>
            </div>
          )}
        </div>
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((e) => !e)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setExpanded((v) => !v); }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 20,
          padding: '8px 14px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          cursor: 'pointer',
          width: 'fit-content',
        }}
      >
        <span style={{ color, display: 'flex', alignItems: 'center' }}>{icon}</span>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <button
          aria-label="Dismiss"
          onClick={(e) => { e.stopPropagation(); dismiss(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, padding: 0, marginLeft: 4, display: 'flex' }}
        >
          <TimesIcon />
        </button>
      </div>
    </div>
  );
};
